import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return url.startsWith("http://") || url.startsWith("https://");
  } catch {
    return false;
  }
};

// Parameter definitions
const parameterDefinitions = [
//   { key: "3A", label: "3A" },
//   { key: "3B", label: "3B" },
//   { key: "3C", label: "3C" },
  { key: "zip_url", label: "Zip URL" },
];

// Default data object
const defaultData = {
  files: {
    "3A": "N/A",
    "3B": "N/A",
    "3C": "N/A",
  },
  zip_url: "N/A",
};

const PeelStrengthTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("PeelStrengthTable props:", { data, startDate, endDate, shifts, subForm });

  // Validate and filter data
  const validData = (data || []).filter((item) => {
    const isValid = item && item.inspectionDate && item.inspectionShift && item.form_data;
    if (!isValid) {
      console.warn("Invalid data item filtered out:", item);
    }
    return isValid;
  });

  // Define static table headers
  const staticHeaders = [
    { id: "parameter", label: "Parameter" },
  ];

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(validData.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Build data map for easier lookup
  const dataMap = useMemo(() => {
    const map = {};
    validData.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) {
        console.warn("Skipping item with invalid date or shift:", item);
        return;
      }
      const key = `${date}_${shift}`;
      map[key] = {
        ...defaultData,
        files: {
          ...defaultData.files,
          ...(item.form_data?.files || {}),
        },
        zip_url: item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url) ? item.zip_url : "N/A",
      };
      console.log(`DataMap entry for ${key}:`, map[key]);
    });
    return map;
  }, [validData]);

  // Create dynamic headers
  const dynamicHeaders = useMemo(() => {
    const headers = [];
    dates.forEach((date) => {
      if (!startDate || !endDate || (date >= formatDate(startDate) && date <= formatDate(endDate))) {
        effectiveShifts.forEach((shift) => {
          headers.push({
            id: `${date}_${shift}`,
            label: `${date} ${shift}`,
            dateShift: `${date} ${shift}`,
          });
        });
      }
    });
    return headers;
  }, [dates, effectiveShifts, startDate, endDate]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: estimateTextWidth("Parameter"),
      value: 100, // Default width for value columns
    };

    // Width for parameter column
    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.min(
        Math.max(widths.parameter, estimateTextWidth(param.label)),
        8 * window.innerWidth / 100
      );
    });

    // Width for value columns based on data
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const entry = dataMap[key] || defaultData;
        const fields = [
          entry.files["3A"] || "N/A",
          entry.files["3B"] || "N/A",
          entry.files["3C"] || "N/A",
          entry.zip_url === "N/A" ? "N/A" : "LINK",
        ];
        fields.forEach((value) => {
          widths.value = Math.min(
            Math.max(
              widths.value,
              estimateTextWidth(value)
            ),
            8 * window.innerWidth / 100
          );
        });
      });
    });

    dynamicHeaders.forEach((header) => {
      widths[header.id] = widths.value;
    });

    return widths;
  }, [dataMap, dynamicHeaders, dates, effectiveShifts]);

  // Calculate left position for sticky column
  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = parameterDefinitions.map((param) => ({
      parameter: param.label,
      data: {},
    }));

    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const entry = dataMap[key] || defaultData;
        parameterDefinitions.forEach((param) => {
          const row = rows.find((r) => r.parameter === param.label);
          row.data[key] = param.key === "zip_url"
            ? entry.zip_url
            : entry.files[param.key] || "N/A";
        });
      });
    });

    return rows;
  }, [dataMap, dates, effectiveShifts]);

  const exportToExcel = () => {
    const headerRow = ["Parameter", ...dynamicHeaders.map(h => h.label)];

    const excelRows = tableRows.map((row) => {
      const rowData = [row.parameter];
      dynamicHeaders.forEach((header) => {
        const value = row.data[header.id] || "N/A";

        // Make zip_url a real clickable hyperlink in Excel
        if (row.parameter === "Zip URL" && value !== "N/A" && value.startsWith("http")) {
          rowData.push(`=HYPERLINK("${value}", "DOWNLOAD")`);
        } else {
          rowData.push(value === "N/A" ? "" : value);
        }
      });
      return rowData;
    });

    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...excelRows]);

    // Auto-size columns
    const colWidths = headerRow.map((_, i) => {
      let max = i === 0 ? 15 : 18;
      excelRows.forEach(r => {
        const val = String(r[i] || "");
        const len = val.startsWith("=HYPERLINK") ? 10 : val.length;
        if (len > max) max = len;
      });
      return { wch: Math.min(max + 4, 60) };
    });
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Peel Strength");

    const fileName = `Peel_Strength_Test_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Peel Strength
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dynamicHeaders.length === 0}
      >
        Download as Excel
      </Button>
      
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              <StickyTableCell
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {dynamicHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={staticHeaders.length + dynamicHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {dynamicHeaders.map((header) => (
                    <BodyTableCell
                      key={`${header.id}_${index}`}
                      columnWidth={columnWidths[header.id]}
                    >
                      {row.parameter === "Zip URL" && row.data[header.id] !== "N/A" ? (
                        <a
                          href={row.data[header.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          LINK
                        </a>
                      ) : (
                        row.data[header.id]
                      )}
                    </BodyTableCell>
                  ))}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PeelStrengthTable;